import { useCallback } from "react";
import * as htmlToImage from "html-to-image";
import html2canvas from "html2canvas";

export function useMapScreenshots({
  maps,
  markers,
  mapRefs,
  setNotification,
  setIsScreenshotting,
  isScreenshotting,
  selectedMap,
  setSelectedMap,
  batchScreenshotIndex,
  setBatchScreenshotIndex,
  useAlternateImages = false,
}) {
  // Single map screenshot
  const handleScreenshotMap = useCallback(
    async (mapKey) => {
      if (!mapKey) return;
      if (isScreenshotting) {
        setNotification("Screenshot already in progress, skipping...");
        return;
      }
      let map = maps.find((m) => m.key === mapKey);
      if (map && useAlternateImages) {
        const img = map.img.replace(/(\.webp|\.png|\.jpg)$/i, "2$1");
        map = { ...map, img };
      }
      if (!map) return;
      setIsScreenshotting(true);
      setNotification(`Downloading ${map.name} map image...`);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const originalMapContainer = mapRefs.current[mapKey];
        if (!originalMapContainer) throw new Error("Map container not found");
        const mapWidth = originalMapContainer.offsetWidth;
        const mapHeight = originalMapContainer.offsetHeight;

        // Create a temporary screenshot container with same dimensions as original
        const screenshotContainer = document.createElement("div");
        screenshotContainer.style.position = "fixed";
        screenshotContainer.style.top = "0";
        screenshotContainer.style.left = "0";
        screenshotContainer.style.width = `${mapWidth}px`;
        screenshotContainer.style.height = `${mapHeight}px`;
        screenshotContainer.style.background = "#000";
        screenshotContainer.style.zIndex = "-1000"; // Hidden but rendered
        screenshotContainer.style.overflow = "hidden";

        // Create map image with same dimensions as original
        const mapImg = document.createElement("img");
        mapImg.src = map.img;
        mapImg.style.width = `${mapWidth}px`;
        mapImg.style.height = `${mapHeight}px`;
        mapImg.style.objectFit = "cover";
        mapImg.style.position = "absolute";
        mapImg.style.top = "0";
        mapImg.style.left = "0";
        screenshotContainer.appendChild(mapImg);

        // Add filtered markers - only show markers with team OR logo (occupied markers)
        const markersToShow = markers.filter((m) => {
          if (m.map !== mapKey) return false;
          const hasTeam = m.team && typeof m.team === "string" && m.team.trim() !== "";
          const hasLogo = m.logo && typeof m.logo === "string" && m.logo.trim() !== "";
          return hasTeam || hasLogo;
        });

        markersToShow.forEach((marker) => {
          // Create main marker container
          const markerContainer = document.createElement("div");
          markerContainer.style.position = "absolute";
          markerContainer.style.left = `${marker.x * 100}%`;
          markerContainer.style.top = `${marker.y * 100}%`;
          markerContainer.style.transform = "translate(-50%, -100%)";
          markerContainer.style.zIndex = "20";
          markerContainer.style.pointerEvents = "none";
          markerContainer.style.userSelect = "none";
          markerContainer.style.display = "flex";
          markerContainer.style.flexDirection = "column";
          markerContainer.style.alignItems = "center";

          // Add tier label above marker if it exists
          if (marker.tier && marker.tier.trim()) {
            const tierLabel = document.createElement("div");
            tierLabel.style.position = "absolute";
            tierLabel.style.top = "-1.3rem";
            tierLabel.style.left = "50%";
            tierLabel.style.transform = "translateX(-50%)";
            tierLabel.style.background = "#151122"; // neprozirna, kao u UI
            tierLabel.style.color = "#ff3461"; // text-primary
            tierLabel.style.border = "none"; // nema crvenog okvira u UI
            tierLabel.style.fontWeight = "800";
            tierLabel.style.fontSize = "0.92rem";
            tierLabel.style.padding = "2px 10px";
            tierLabel.style.borderRadius = "7px";
            tierLabel.style.boxShadow = "0 2px 8px #ff346155";
            tierLabel.style.zIndex = "100";
            tierLabel.style.whiteSpace = "nowrap";
            tierLabel.textContent = marker.tier;
            markerContainer.appendChild(tierLabel);
          }

          // --- LOGO ili CIRCLE ---
          if (marker.logo && marker.logo.trim()) {
            // LOGO marker
            const logoWrapper = document.createElement("div");
            logoWrapper.style.position = "relative";
            logoWrapper.style.display = "inline-block";
            logoWrapper.style.width = `${80 * (marker.size || 1)}px`;
            logoWrapper.style.height = `${80 * (marker.size || 1)}px`;
            logoWrapper.style.borderRadius = "50%";
            logoWrapper.style.overflow = "hidden";
            logoWrapper.style.background = "rgba(35,36,77,0.68)";
            logoWrapper.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
            logoWrapper.style.border = "2px solid var(--color-primary)";
            logoWrapper.style.maxWidth = "100%";
            logoWrapper.style.maxHeight = "100%";
            logoWrapper.style.transition = "transform 0.2s ease";

            const logoImg = document.createElement("img");
            logoImg.src = marker.logo;
            logoImg.style.width = "100%";
            logoImg.style.height = "100%";
            logoImg.style.objectFit = "cover";
            logoImg.style.borderRadius = "50%";
            logoImg.style.display = "block";
            logoImg.style.background = "rgba(35,36,77,0.68)";
            logoImg.style.position = "relative";
            logoImg.style.zIndex = "2";
            logoWrapper.appendChild(logoImg);

            // FLAG overlay (kao u UI, full-size, ispod imena tima)
            if (marker.flag && marker.flag.trim()) {
              const flagImg = document.createElement("img");
              flagImg.src = marker.flag;
              flagImg.alt = "flag";
              flagImg.style.position = "absolute";
              flagImg.style.top = "0";
              flagImg.style.left = "0";
              flagImg.style.width = "100%";
              flagImg.style.height = "100%";
              flagImg.style.objectFit = "cover";
              flagImg.style.borderRadius = "50%";
              flagImg.style.background = "rgba(35,36,77,0.68)";
              flagImg.style.border = "2px solid var(--color-primary)";
              flagImg.style.zIndex = "3";
              logoWrapper.appendChild(flagImg);
            }

            // IME TIMA (iznad flag-a, z-index 10)
            if (marker.team && marker.team.trim()) {
              const teamText = document.createElement("span");
              teamText.textContent = marker.team;
              teamText.style.display = "flex";
              teamText.style.alignItems = "center";
              teamText.style.justifyContent = "center";
              teamText.style.fontWeight = "800";
              // Font size scaling kao u Marker.js
              const teamLength = marker.team.length;
              let fontSize = "1.09rem";
              if (teamLength > 32) fontSize = "0.44rem";
              else if (teamLength > 26) fontSize = "0.52rem";
              else if (teamLength > 22) fontSize = "0.60rem";
              else if (teamLength > 18) fontSize = "0.68rem";
              else if (teamLength > 14) fontSize = "0.78rem";
              else if (teamLength > 11) fontSize = "0.88rem";
              else if (teamLength > 8) fontSize = "0.99rem";
              teamText.style.fontSize = fontSize;
              teamText.style.color = "#fff";
              teamText.style.whiteSpace = "nowrap";
              teamText.style.maxWidth = "88%";
              teamText.style.padding = "0 6%";
              teamText.style.overflow = "visible";
              teamText.style.userSelect = "none";
              teamText.style.pointerEvents = "none";
              teamText.style.textAlign = "center";
              teamText.style.position = "absolute";
              teamText.style.left = "50%";
              teamText.style.top = "50%";
              teamText.style.transform = "translate(-50%, -50%) scaleY(1.5)";
              teamText.style.zIndex = "10";
              teamText.style.margin = "0";
              teamText.style.height = "auto";
              teamText.style.lineHeight = "1";
              teamText.style.textShadow =
                "0 0 2px #000, 0 1px 8px #0008, 2px 2px 8px rgba(0,0,0,0.95), 0 0 6px #000, 0 0 12px #000, 0 0 1px #000";
              logoWrapper.appendChild(teamText);
            }

            // SLOT badge (iznad zastave, ispod imena tima)
            if (marker.slot) {
              const slotBadge = document.createElement("div");
              slotBadge.textContent = `Slot ${String(marker.slot)}`;
              slotBadge.style.position = "absolute";
              slotBadge.style.left = "50%";
              slotBadge.style.top = "50%";
              slotBadge.style.transform = "translate(-50%, 35%)";
              slotBadge.style.background = "rgba(21,17,34,0.85)";
              slotBadge.style.color = "#ff3461";
              slotBadge.style.fontWeight = "800";
              slotBadge.style.fontSize = "0.78rem";
              slotBadge.style.padding = "2px 8px";
              slotBadge.style.borderRadius = "8px";
              slotBadge.style.zIndex = "7";
              slotBadge.style.whiteSpace = "nowrap";
              slotBadge.style.pointerEvents = "none";
              slotBadge.style.border = "1px solid rgba(255,52,97,0.6)";
              slotBadge.style.boxShadow = "0 2px 6px rgba(0,0,0,0.6)";
              logoWrapper.appendChild(slotBadge);
            }
            markerContainer.appendChild(logoWrapper);
          } else {
            // CIRCLE marker (bez loga)
            const markerEl = document.createElement("div");
            markerEl.style.width = `${80 * (marker.size || 1)}px`;
            markerEl.style.height = `${80 * (marker.size || 1)}px`;
            markerEl.style.border = "2px solid var(--color-primary)";
            markerEl.style.borderRadius = "50%";
            markerEl.style.display = "flex";
            markerEl.style.alignItems = "center";
            markerEl.style.justifyContent = "center";
            markerEl.style.lineHeight = "1";
            markerEl.style.position = "relative";
            markerEl.style.textAlign = "center";
            markerEl.style.overflow = "visible";
            markerEl.style.background = "rgba(35,36,77,0.68)";
            markerEl.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
            markerEl.style.maxWidth = "100%";
            markerEl.style.maxHeight = "100%";
            markerEl.style.transition = "transform 0.2s ease";

            // FLAG overlay (kao u UI, full-size)
            if (marker.flag && marker.flag.trim()) {
              const flagImg = document.createElement("img");
              flagImg.src = marker.flag;
              flagImg.alt = "flag";
              flagImg.style.position = "absolute";
              flagImg.style.top = "0";
              flagImg.style.left = "0";
              flagImg.style.width = "100%";
              flagImg.style.height = "100%";
              flagImg.style.objectFit = "cover";
              flagImg.style.borderRadius = "50%";
              flagImg.style.background = "rgba(35,36,77,0.68)";
              flagImg.style.border = "2px solid var(--color-primary)";
              flagImg.style.zIndex = "3";
              markerEl.appendChild(flagImg);
            }

            // IME TIMA (iznad flag-a, z-index 10)
            if (marker.team && marker.team.trim()) {
              const teamText = document.createElement("span");
              teamText.textContent = marker.team;
              teamText.style.display = "flex";
              teamText.style.alignItems = "center";
              teamText.style.justifyContent = "center";
              teamText.style.fontWeight = "800";
              // Font size scaling kao u Marker.js
              const teamLength = marker.team.length;
              let fontSize = "1.09rem";
              if (teamLength > 32) fontSize = "0.44rem";
              else if (teamLength > 26) fontSize = "0.52rem";
              else if (teamLength > 22) fontSize = "0.60rem";
              else if (teamLength > 18) fontSize = "0.68rem";
              else if (teamLength > 14) fontSize = "0.78rem";
              else if (teamLength > 11) fontSize = "0.88rem";
              else if (teamLength > 8) fontSize = "0.99rem";
              teamText.style.fontSize = fontSize;
              teamText.style.color = "#fff";
              teamText.style.whiteSpace = "nowrap";
              teamText.style.maxWidth = "88%";
              teamText.style.padding = "0 6%";
              teamText.style.overflow = "visible";
              teamText.style.userSelect = "none";
              teamText.style.pointerEvents = "none";
              teamText.style.textAlign = "center";
              teamText.style.position = "absolute";
              teamText.style.left = "50%";
              teamText.style.top = "50%";
              teamText.style.transform = "translate(-50%, -50%) scaleY(1.5)";
              teamText.style.zIndex = "10";
              teamText.style.margin = "0";
              teamText.style.height = "auto";
              teamText.style.lineHeight = "1";
              teamText.style.textShadow =
                "0 0 2px #000, 0 1px 8px #0008, 2px 2px 8px rgba(0,0,0,0.95), 0 0 6px #000, 0 0 12px #000, 0 0 1px #000";
              markerEl.appendChild(teamText);
            }

            // SLOT badge (iznad zastave, ispod imena tima)
            if (marker.slot) {
              const slotBadge = document.createElement("div");
              slotBadge.textContent = `Slot ${String(marker.slot)}`;
              slotBadge.style.position = "absolute";
              slotBadge.style.left = "50%";
              slotBadge.style.top = "50%";
              slotBadge.style.transform = "translate(-50%, 35%)";
              slotBadge.style.background = "rgba(21,17,34,0.85)";
              slotBadge.style.color = "#ff3461";
              slotBadge.style.fontWeight = "800";
              slotBadge.style.fontSize = "0.78rem";
              slotBadge.style.padding = "2px 8px";
              slotBadge.style.borderRadius = "8px";
              slotBadge.style.zIndex = "7";
              slotBadge.style.whiteSpace = "nowrap";
              slotBadge.style.pointerEvents = "none";
              slotBadge.style.border = "1px solid rgba(255,52,97,0.6)";
              slotBadge.style.boxShadow = "0 2px 6px rgba(0,0,0,0.6)";
              markerEl.appendChild(slotBadge);
            }
            markerContainer.appendChild(markerEl);
          }
          screenshotContainer.appendChild(markerContainer);
        });

        // Add watermark
        const watermark = document.createElement("div");
        watermark.style.position = "absolute";
        watermark.style.bottom = "8px";
        watermark.style.right = "16px";
        watermark.style.color = "#ff3461";
        watermark.style.fontSize = "16px";
        watermark.style.fontWeight = "bold";
        watermark.style.textShadow = "2px 2px 4px rgba(0,0,0,0.8)";
        watermark.style.zIndex = "20";
        watermark.textContent = "made by ewave.gg";
        screenshotContainer.appendChild(watermark);

        // Add logo to bottom-left corner
        const logoContainer = document.createElement("div");
        logoContainer.style.position = "absolute";
        logoContainer.style.bottom = "16px";
        logoContainer.style.left = "16px";
        logoContainer.style.zIndex = "20";
        const logoImg = document.createElement("img");
        logoImg.src = "https://i.imgur.com/oaLISn2.png";
        logoImg.style.width = "80px";
        logoImg.style.height = "80px";
        logoImg.style.objectFit = "contain";
        logoImg.style.opacity = "0.9";
        logoImg.style.filter = "drop-shadow(2px 2px 4px rgba(0,0,0,0.8))";
        logoImg.onerror = () => {
          logoContainer.style.display = "none";
        };
        logoContainer.appendChild(logoImg);
        screenshotContainer.appendChild(logoContainer);

        // Add to document
        document.body.appendChild(screenshotContainer);

        // Wait for image to load
        await new Promise((resolve) => {
          if (mapImg.complete) {
            resolve();
          } else {
            mapImg.onload = resolve;
            mapImg.onerror = resolve;
          }
        });

        // Wait a bit more for everything to render
        await new Promise((resolve) => setTimeout(resolve, 150));

        // Show loading indicator
        const loadingIndicator = document.createElement("div");
        loadingIndicator.style.position = "fixed";
        loadingIndicator.style.top = "50%";
        loadingIndicator.style.left = "50%";
        loadingIndicator.style.transform = "translate(-50%, -50%)";
        loadingIndicator.style.background = "#100c1e";
        loadingIndicator.style.color = "white";
        loadingIndicator.style.padding = "20px 30px";
        loadingIndicator.style.borderRadius = "12px";
        loadingIndicator.style.border = "2px solid #ff3461";
        loadingIndicator.style.zIndex = "10000";
        loadingIndicator.style.fontWeight = "bold";
        loadingIndicator.style.fontSize = "16px";
        loadingIndicator.textContent = "📋 Preparing...";
        document.body.appendChild(loadingIndicator);

        let dataUrl;
        try {
          // Primary method: html-to-image on the screenshot container
          dataUrl = await htmlToImage.toPng(screenshotContainer, {
            quality: 1.0,
            pixelRatio: 1,
            width: mapWidth,
            height: mapHeight,
            skipAutoScale: true,
            cacheBust: true,
            backgroundColor: "#000000",
          });
          if (!dataUrl || dataUrl === "data:," || dataUrl.length < 100) {
            throw new Error("html-to-image generated empty or invalid image");
          }
        } catch (htmlToImageError) {
          try {
            const canvas = await html2canvas(screenshotContainer, {
              backgroundColor: "#000000",
              scale: 1,
              width: mapWidth,
              height: mapHeight,
              useCORS: true,
              allowTaint: true,
              foreignObjectRendering: true,
              logging: false,
            });
            dataUrl = canvas.toDataURL("image/png", 1.0);
          } catch (html2canvasError) {
            throw new Error("Screenshot capture failed with both methods");
          }
        }

        // Clean up
        if (screenshotContainer && screenshotContainer.parentNode) {
          screenshotContainer.parentNode.removeChild(screenshotContainer);
        }
        if (loadingIndicator && loadingIndicator.parentNode) {
          loadingIndicator.parentNode.removeChild(loadingIndicator);
        }

        // Download the image
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `lootspot_${mapKey}_${new Date().toISOString().slice(0, 10)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setNotification(`${map.name} map image saved!`);
      } catch (error) {
        setNotification(`Error creating map image: ${error.message}`);
      } finally {
        setIsScreenshotting(false);
      }
    },
    [
      maps,
      markers,
      mapRefs,
      setNotification,
      setIsScreenshotting,
      isScreenshotting,
      useAlternateImages,
    ],
  );

  // Batch screenshot svih mapa
  const handleScreenshotAllMaps = useCallback(async () => {
    if (maps.length === 0) {
      setNotification("No maps available for download");
      return;
    }
    if (isScreenshotting) {
      setNotification("Map image download already in progress...");
      return;
    }
    const mapOrder = ["erangel", "miramar", "taego", "rondo"];
    const sortedMaps = mapOrder
      .map((key) => maps.find((map) => map.key === key))
      .filter((map) => map !== undefined);
    if (sortedMaps.length === 0) {
      setNotification("No valid maps found for download");
      return;
    }
    setNotification("Starting batch download...");
    const originalSelectedMap = selectedMap;
    for (let i = 0; i < sortedMaps.length; i++) {
      const map = sortedMaps[i];
      setNotification(`Downloading map ${i + 1}/${sortedMaps.length}: ${map.name}...`);
      setSelectedMap(map.key);
      await new Promise((resolve) => setTimeout(resolve, 400));
      await handleScreenshotMap(map.key);
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    setSelectedMap(originalSelectedMap);
    setNotification(`All ${sortedMaps.length} maps downloaded successfully!`);
  }, [maps, isScreenshotting, handleScreenshotMap, selectedMap, setSelectedMap, setNotification]);

  return { handleScreenshotMap, handleScreenshotAllMaps };
}
